'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Eye, Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  usePlaybookTemplate,
  useUpdateTemplate,
} from '@/features/playbooks/api/use-playbook-templates';
import { Nis2WorkflowTemplateMatrix } from '@/features/playbooks/components/Nis2WorkflowTemplateMatrix';
import { TemplateBuilderForm } from '@/features/playbooks/components/TemplateBuilderForm';
import { WorkflowTemplateOverview } from '@/features/playbooks/components/WorkflowTemplateOverview';
import { WorkflowSectionTabs } from '@/features/playbooks/components/WorkflowSectionTabs';
import type { CreatePhaseInput, PlaybookDomain } from '@/features/playbooks/types';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/tasks/playbooks/templates/$templateId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.editWorkflowTemplate'),
  }),
  component: EditTemplatePage,
});

function EditTemplatePage() {
  const { t } = useTranslation('tasks');
  const { templateId } = Route.useParams();
  const navigate = useNavigate();
  const { template, isLoading } = usePlaybookTemplate(templateId);
  const { updateTemplate, isUpdating, isOrgReady } = useUpdateTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [domain, setDomain] = useState<PlaybookDomain>('custom');
  const [phases, setPhases] = useState<CreatePhaseInput[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (template && !initialized) {
      setName(template.name);
      setDescription(template.description ?? '');
      setDomain(template.domain);
      setPhases(
        template.phases.map((p) => ({
          name: p.name,
          description: p.description,
          sortOrder: p.sortOrder,
          obligations: p.obligations.map((o) => ({
            name: o.name,
            description: o.description,
            instructions: o.instructions,
            cadence: o.cadence,
            defaultPriority: o.defaultPriority,
            defaultAssigneeRole: o.defaultAssigneeRole,
            defaultAssigneeId: o.defaultAssigneeId,
            defaultDueOffsetDays: o.defaultDueOffsetDays,
            evidenceConfig: o.evidenceConfig,
            requiresApproval: o.requiresApproval,
            regulatoryReference: o.regulatoryReference,
            taskType: o.taskType ?? null,
            deliverableFormat: o.deliverableFormat ?? null,
            datapointLinkConfig: o.datapointLinkConfig ?? null,
          })),
        }))
      );
      setInitialized(true);
    }
  }, [template, initialized]);

  const isReadOnly = template?.isSystem ?? false;

  const handleSave = async () => {
    if (!name.trim() || isReadOnly) return;
    if (!isOrgReady) {
      toast.error(t('tasks:toast.noActiveOrganizationPleaseSelectAnOrganizationFirst'));
      return;
    }
    // Client-side validation: check that all phases and obligations have names.
    for (const phase of phases) {
      if (!phase.name.trim()) {
        toast.error(t('tasks:toast.allPhasesMustHaveAName'));
        return;
      }
      for (const obl of phase.obligations) {
        if (!obl.name.trim()) {
          toast.error(t('tasks:toast.allObligationsInPhaseMustHaveAName', { phase: phase.name }));
          return;
        }
      }
    }
    try {
      await updateTemplate({
        templateId,
        data: {
          name: name.trim(),
          description: description.trim() || null,
          domain,
          metadata: {},
          phases,
        },
      });
      toast.success(t('tasks:toast.changesHaveBeenSaved'));
    } catch (err) {
      const message = err instanceof Error ? err.message : t('editTemplate.failedToSave');
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">{t('editTemplate.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <WorkflowSectionTabs activeTab="templates" />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void navigate({ to: '/tasks/playbooks/templates' })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {isReadOnly ? t('editTemplate.viewTitle') : t('editTemplate.editTitle')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isReadOnly
                ? t('editTemplate.readOnlyDescription')
                : t('editTemplate.editDescription')}
            </p>
          </div>
        </div>
        {isReadOnly ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() =>
                void navigate({
                  to: '/tasks/playbooks/activate',
                  search: { templateId },
                })
              }
            >
              {t('activate.startWorkflowRun')}
            </Button>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Eye className="h-4 w-4" />
              {t('editTemplate.readOnly')}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() =>
                void navigate({
                  to: '/tasks/playbooks/activate',
                  search: { templateId },
                })
              }
            >
              {t('activate.startWorkflowRun')}
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={isUpdating || !name.trim() || !isOrgReady}
              data-testid="save-template-btn"
            >
              {isUpdating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {t('editTemplate.saveChanges')}
            </Button>
          </div>
        )}
      </div>

      {domain !== 'nis2' && phases.length > 0 ? <WorkflowTemplateOverview phases={phases} /> : null}

      {domain === 'nis2' ? <Nis2WorkflowTemplateMatrix /> : null}

      {domain !== 'nis2' ? (
        <TemplateBuilderForm
          name={name}
          description={description}
          domain={domain}
          phases={phases}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          onDomainChange={setDomain}
          onPhasesChange={setPhases}
          readOnly={isReadOnly}
        />
      ) : null}
    </div>
  );
}
